export const LEGAL_REGIONS = {
  in: { label: "India", name: "Indian Residents" },
  eu: { label: "Europe", name: "Europe (EEA)" },
  uk: { label: "UK", name: "United Kingdom" },
  na: { label: "USA & Canada", name: "USA & Canada" },
  row: { label: "Others", name: "Rest of the World" },
};

export const LEGAL_UPDATED = "September 2026";
export const LEGAL_CONTACT = "invest@thelifespanproject.com";

export const LEGAL_PAGES = {
  terms: {
    slug: "terms",
    eyebrow: "Legal",
    title: "Terms of Membership",
    intro:
      "These Terms govern your membership of The Lifespan Project. By subscribing, paying or becoming a member, you accept them. Please read them carefully; they are written in plain English on purpose.",
    sections: [
      {
        h: "1. Who we are",
        body: [
          "The Lifespan Project is a for-profit research and development (R&D) organization working on the science of human longevity across physics, artificial intelligence, biology and allied disciplines. The Lifespan Labs is its upcoming marketplace for certified products, technologies and services.",
          "Everything we offer today is research in progress. This website and your membership are not a medical service, a health product or a finished scientific solution.",
        ],
      },
      {
        h: "2. Membership",
        body: [
          "Membership is a personal, non-transferable digital grant. Three tiers exist: Wellwisher Founding Member (free, research news and updates), Prime Founding Member (monthly) and Pro Founding Member (one-time lifetime).",
          "Every member receives a digital membership card with a unique membership ID and a scannable barcode. Cards and IDs belong to the named member only and may not be sold, shared or transferred.",
        ],
      },
      {
        h: "3. Payments",
        body: [
          "Prices are shown in Indian Rupees (₹) for Indian residents and in US Dollars ($) for members outside India. You confirm your region at checkout, and you must select the region of your actual residence.",
          "Payments are processed securely by Stripe. Payment gateway access and payment technology contribution is provided by Addrish Software Solutions Pvt Ltd (India), a technology contributor to The Lifespan Project alongside Ajubax Ai Inc. (Canada). Your full card details are handled only by Stripe; we never see, store or process them.",
          "Prime renews every month until you cancel. Pro is a single lifetime payment. Wellwisher is free.",
        ],
      },
      {
        h: "4. Research status and outcomes",
        body: [
          "All research, programs and solutions described by The Lifespan Project are works in progress. We do not guarantee any scientific result, health outcome, product availability or extension of human lifespan.",
          "Nothing on this website, in member content or in our emails is medical, health, financial or investment advice. Membership supports the research mission; it is not an investment, a security, a donation, or a promise of any return, equity or profit share.",
        ],
      },
      {
        h: "5. Acceptable use",
        body: [
          "You agree to use this website and your membership lawfully; not to misrepresent your identity or region; not to misuse membership cards, IDs or barcodes; and not to republish member-only content as your own.",
        ],
      },
      {
        h: "6. Intellectual property",
        body: [
          "All content, research material, branding, names and designs remain the property of The Lifespan Project. Members receive a personal, non-commercial right to view member content.",
        ],
      },
      {
        h: "7. Limitation of liability",
        body: [
          "To the maximum extent permitted by applicable law, The Lifespan Project is not liable for indirect, incidental or consequential damages. Our total liability to you is limited to the amount you paid us in the twelve months before the claim.",
        ],
      },
      {
        h: "8. Cancellation and termination",
        body: [
          "You may cancel a Prime membership at any time by emailing us; cancellation stops future monthly renewals. We may suspend or terminate a membership for fraud, misuse or breach of these Terms.",
        ],
      },
      {
        h: "9. Changes to these Terms",
        body: [
          "We may update these Terms from time to time. The latest version is always on this page with its date. Continued membership after an update means acceptance.",
        ],
      },
    ],
    regional: {
      in: [
        "These Terms are governed by the laws of India, and the courts of India have jurisdiction, subject to your rights under the Consumer Protection Act, 2019.",
        "Grievances: write to invest@thelifespanproject.com with 'Grievance' in the subject. We acknowledge and resolve grievances within the timelines required by the Information Technology Act, 2000 and the Consumer Protection (E-Commerce) Rules, 2020.",
      ],
      eu: [
        "If you are a consumer in the EEA, these Terms apply without prejudice to the mandatory consumer protections of your member state and to your rights under the GDPR.",
        "Governing law: the laws of India, unless mandatory law in your country of residence gives you additional rights; those rights remain fully intact.",
      ],
      uk: [
        "Your statutory rights under the Consumer Rights Act 2015 and the UK GDPR are unaffected by these Terms.",
        "Governing law: the laws of India, unless mandatory UK law gives you additional rights; those rights remain fully intact.",
      ],
      na: [
        "These Terms are governed by the laws of India, except where your state or province requires otherwise.",
        "California residents retain all CCPA/CPRA rights and Canadian residents retain all PIPEDA rights described in our Privacy Policy.",
      ],
      row: [
        "These Terms are governed by the laws of India, except where mandatory local law in your country grants you additional rights; those rights remain fully intact.",
        "We follow internationally accepted principles of fair dealing and consumer protection for members everywhere.",
      ],
    },
  },

  privacy: {
    slug: "privacy",
    eyebrow: "Legal",
    title: "Privacy Policy & Data Usage",
    intro:
      "This policy explains what data The Lifespan Project collects, why, how it is protected, and the rights you have, under Indian law and international law.",
    sections: [
      {
        h: "1. What we collect",
        body: [
          "Identity and contact data: name, email, phone number, country and city, when you subscribe, join, invest or become a member.",
          "Membership data: your tier, membership ID and card details. Payment metadata: plan, amount, currency and payment status. Your full card number is never seen or stored by us; it stays with Stripe.",
          "Content you send us: enquiries, applications and messages. Technical data: browser, device and privacy-respecting analytics.",
        ],
      },
      {
        h: "1A. Level 0 Assessment data",
        body: [
          "When you take the free Level 0 Assessment, we store your answers, your computed markers, and the contact details you provide (name, email, phone/WhatsApp number, country and how you found us), together with your consent timestamp and copy version. Health-related answers are treated as sensitive and are never shared with third parties for marketing.",
          "We use this data to show and email your Lifespan Markers report, to contact you by email and WhatsApp/phone as per your consent, and to improve the assessment. You may withdraw consent or request deletion of your assessment data at any time by emailing invest@thelifespanproject.com; we delete or anonymise assessment records within the retention period stated on request, and always honor unsubscribe requests.",
          "This processing is done with your explicit consent under the India DPDP Act 2023 and, where applicable, the GDPR/UK GDPR lawful basis of consent. You have the rights of access, correction, erasure and grievance redress described in this policy.",
        ],
      },
      {
        h: "1B. Mental Marker Scan (Level 1) data",
        body: [
          "The Level 1 Mental Marker Scan uses your device camera and microphone. Face landmarks, color sampling and voice capture are processed on your device; raw video and audio are never uploaded or stored by us. A single face photo is captured during the scan and stored so it can appear on your Current State card, which you can download from the website and receive by email.",
          "We store the final derived readings (eye color label, face hue label, tongue color label, expression label, teeth and voice capture flags), your check-in answers, your Mental Marker band, the scan photo, and the details you provide (name, email, phone/WhatsApp number, age, sex, city, country and how you found us), together with your consent timestamp and copy version.",
          "You can view and permanently delete your scan data, including the photo, at any time from your saved results link, or by emailing invest@thelifespanproject.com. This is a wellness reflection tool, not a medical or psychological diagnosis.",
        ],
      },
      {
        h: "2. How we use your data",
        body: [
          "To create and manage your membership, issue your digital membership card and barcode, send receipts, research news and updates you asked for, respond to your enquiries, improve this website, and meet legal obligations.",
          "We do not sell your personal data. We do not profile you for advertising.",
        ],
      },
      {
        h: "3. Who processes your data",
        body: [
          "Stripe processes payments; payment gateway access is contributed by Addrish Software Solutions Pvt Ltd (India). Transactional email is sent through our email delivery provider. Hosting and analytics run on secure cloud infrastructure.",
          "Ajubax Ai Inc. (Canada) and Addrish Software Solutions Pvt Ltd (India) act as technology contributors and are bound by confidentiality and data-protection obligations.",
        ],
      },
      {
        h: "4. Storage and international transfer",
        body: [
          "Data is stored on secure cloud servers and may be processed in India and other countries where our providers operate, with appropriate contractual and technical safeguards.",
        ],
      },
      {
        h: "5. Retention",
        body: [
          "We keep your data while your membership is active and afterwards only as long as the law requires (for example, payment records for tax and accounting).",
        ],
      },
      {
        h: "6. Security",
        body: [
          "We use encryption in transit, access controls and least-privilege practices. No system is perfectly secure; if a breach affects you, we will inform you as the law requires.",
        ],
      },
      {
        h: "7. Your rights",
        body: [
          "Wherever you live, you may request access to, correction of, or deletion of your personal data, and withdraw consent, by emailing invest@thelifespanproject.com. Region-specific rights are listed below.",
        ],
      },
      {
        h: "8. Cookies and analytics",
        body: [
          "We use privacy-respecting product analytics to understand site usage. We do not run third-party advertising trackers.",
        ],
      },
      {
        h: "9. Age",
        body: [
          "Membership is intended for adults (18 years or the age of majority in your country). We do not knowingly collect data from children.",
        ],
      },
    ],
    regional: {
      in: [
        "Under the Digital Personal Data Protection Act, 2023 (DPDP Act), you are a Data Principal with rights to access, correction, erasure, nomination and grievance redressal. We process personal data with your consent and for legitimate uses permitted by the Act.",
        "Grievance Officer: email invest@thelifespanproject.com with 'Grievance' in the subject. We respond within the statutory period.",
      ],
      eu: [
        "Under the GDPR, our legal bases are your consent (Art. 6(1)(a)) and performance of a contract (Art. 6(1)(b)).",
        "You have the rights of access, rectification, erasure, restriction, portability and objection, and the right to complain to your national supervisory authority. You may withdraw consent at any time by email.",
      ],
      uk: [
        "Under the UK GDPR and the Data Protection Act 2018 you hold the same rights as under the GDPR.",
        "You may complain to the Information Commissioner's Office (ico.org.uk).",
      ],
      na: [
        "California residents (CCPA/CPRA): you have the right to know, correct, delete and to opt out of sale or sharing; we do not sell or share personal data for advertising.",
        "Canada (PIPEDA): you have rights of access and correction, and may complain to the Office of the Privacy Commissioner of Canada.",
      ],
      row: [
        "We follow OECD-aligned privacy principles for members in every other country, and we honour access, correction and deletion requests from anywhere in the world by email.",
      ],
    },
  },

  refund: {
    slug: "refund",
    eyebrow: "Legal",
    title: "Refund Policy",
    intro:
      "The short version: all memberships are digital membership grants delivered instantly, and we do not offer refunds. Please read this policy before paying.",
    sections: [
      {
        h: "1. No-refund policy",
        body: [
          "Every paid membership is a digital membership grant: your membership ID and digital card are issued immediately upon payment, and digital access cannot be returned. For this reason, all payments are final and non-refundable.",
          "Membership is a grant that supports research in progress; it is not a physical product, a deposit, or a service delivered over time.",
        ],
      },
      {
        h: "2. Prime (monthly)",
        body: [
          "You may cancel Prime at any time by emailing invest@thelifespanproject.com. Cancellation stops all future monthly renewals. The amount for the current, already-started month is not refunded.",
        ],
      },
      {
        h: "3. Pro (lifetime)",
        body: [
          "Pro is a single, one-time lifetime payment. It is non-refundable once your membership ID and card have been issued.",
        ],
      },
      {
        h: "4. Wellwisher (free)",
        body: [
          "Wellwisher membership is free: there is no payment and nothing to refund. You may unsubscribe at any time.",
        ],
      },
      {
        h: "5. Genuine payment errors",
        body: [
          "Duplicate charges, charges where no membership was issued, and clear technical errors are always corrected. Email invest@thelifespanproject.com within 7 days with your receipt; verified errors are reversed to the original payment method within 7 to 14 business days.",
        ],
      },
      {
        h: "6. Your statutory rights",
        body: [
          "Where the law of your country grants mandatory refund or withdrawal rights that cannot be waived, we honour them in full.",
        ],
      },
    ],
    regional: {
      in: [
        "India: under the Consumer Protection Act, 2019 and the Consumer Protection (E-Commerce) Rules, 2020, digital services delivered instantly with your consent are treated as delivered. Because your membership ID and digital card are issued immediately upon payment, this no-refund policy applies. Wrongful or duplicate debits are always corrected.",
      ],
      eu: [
        "Europe: distance contracts normally carry a 14-day withdrawal right. By purchasing, you expressly consent to immediate delivery of digital content (membership ID, card and access) and acknowledge that you lose the withdrawal right once delivery begins (Article 16(m), Consumer Rights Directive 2011/83/EU).",
      ],
      uk: [
        "United Kingdom: under the Consumer Contracts Regulations 2013, the 14-day cancellation right is lost once you agree to the immediate supply of digital content. Payment errors remain refundable in all cases.",
      ],
      na: [
        "USA & Canada: all sales are final, except where state or provincial law requires otherwise. Payment errors and unauthorized charges are always corrected.",
      ],
      row: [
        "Rest of the world: no refunds, except where your local law mandatorily requires one; in which case we comply with that law.",
      ],
    },
  },

  faq: {
    slug: "faq",
    eyebrow: "Answers",
    title: "Frequently Asked Questions",
    intro:
      "Honest answers, in plain English, about who we are, where the work stands, and how membership works.",
    sections: [
      {
        h: "Is The Lifespan Project a for-profit organization?",
        body: [
          "Yes. The Lifespan Project is a for-profit research and development organization. Founding memberships, backers, investors and, in future, certified products on The Lifespan Labs marketplace fund the research. We are not a charity or an NGO, and membership is neither a donation nor an investment.",
        ],
      },
      {
        h: "Is the research complete? Are solutions available today?",
        body: [
          "No. All research and computing programs are work in progress, running at subliminal scale. Our first solutions are targeted to roll out within about 24 months. We do not guarantee any scientific result, product availability or health outcome.",
        ],
      },
      {
        h: "Will membership increase my lifespan?",
        body: [
          "We make no such guarantee, and nothing we publish is medical advice. Membership connects you to the research mission, its publications and its ecosystem. Please consult qualified medical professionals for health decisions.",
        ],
      },
      {
        h: "What are the membership types?",
        body: [
          "Wellwisher Founding Member, free forever: research news and updates, plus a digital membership card. Prime Founding Member, monthly: all published research, content and videos, Labs access at launch, member offers and research submission. Pro Founding Member, one-time lifetime: everything in Prime plus pro-only releases, worldwide event invitations, enhanced discounts and priority access.",
        ],
      },
      {
        h: "Who processes my payment?",
        body: [
          "Payments are processed securely by Stripe. Payment gateway access and payment technology contribution is provided by Addrish Software Solutions Pvt Ltd (India), a technology contributor to the Project alongside Ajubax Ai Inc. (Canada). We never see or store your full card number.",
        ],
      },
      {
        h: "Can I get a refund?",
        body: [
          "No. Memberships are digital grants delivered instantly, so all payments are final. Prime can be cancelled anytime to stop future renewals, and genuine payment errors are always corrected. See the Refund Policy for details and your statutory rights.",
        ],
      },
      {
        h: "What is the barcode on my membership card?",
        body: [
          "Your digital membership card carries your unique membership ID and a scannable barcode. It identifies you as a member; keep it handy for member check-ins, verification and benefits when The Lifespan Labs launches.",
        ],
      },
      {
        h: "How do I cancel Prime?",
        body: [
          "Email invest@thelifespanproject.com from your registered email address. Cancellation stops future monthly renewals; the current month is not refunded.",
        ],
      },
      {
        h: "How is my data handled?",
        body: [
          "We collect only what membership needs, we never sell personal data, and you may request access, correction or deletion at any time. Read the Privacy Policy for your rights under Indian and international law.",
        ],
      },
      {
        h: "How do I contact the Project?",
        body: [
          "Write to invest@thelifespanproject.com for membership, research, investor or grievance matters. We reply personally.",
        ],
      },
    ],
    regional: {
      in: [
        "Indian residents: prices are charged in Indian Rupees: Prime ₹1,900 per month and Pro ₹1,90,000 lifetime, through the payment gateway contributed by Addrish Software Solutions Pvt Ltd. Applicable taxes apply as per Indian law. Your rights under the Consumer Protection Act, 2019 and the DPDP Act, 2023 are fully respected.",
      ],
      eu: [
        "European members: prices are charged in US Dollars: Prime $20 per month and Pro $2,000 lifetime. Your bank may apply currency conversion. Your GDPR and EU consumer rights are fully respected.",
      ],
      uk: [
        "UK members: prices are charged in US Dollars: Prime $20 per month and Pro $2,000 lifetime. Your bank may apply currency conversion. Your UK GDPR and Consumer Rights Act 2015 rights are fully respected.",
      ],
      na: [
        "USA & Canada members: prices are charged in US Dollars: Prime $20 per month and Pro $2,000 lifetime. Your CCPA/CPRA or PIPEDA rights are fully respected.",
      ],
      row: [
        "Members everywhere else: prices are charged in US Dollars: Prime $20 per month and Pro $2,000 lifetime. Your bank may apply currency conversion or local taxes. Mandatory rights under your local law are fully respected.",
      ],
    },
  },
};
